// Freshdesk Omni web chat for the web receipt — DASH-6570.
// The widget is loaded lazily on the first Chat tap, never shows Freshdesk's own
// floating launcher, and is always in English (DEC-5). Chat is only offered when
// the three REACT_APP_FRESHDESK_CHAT_* values are set, which doubles as the
// release switch: unset them and the Support pop-up is back to Call + WhatsApp.

declare global {
  interface Window {
    fdWidget?: {
      init: (config: Record<string, unknown>) => void;
      open: () => void;
      close?: () => void;
      isOpen?: () => boolean;
      setConfig?: (config: Record<string, unknown>) => void;
      ticket?: { setProperties: (props: Record<string, unknown>) => void };
      user?: { clear?: () => unknown };
    };
  }
}

const SCRIPT_ID = "Freshdesk-js-sdk";
const CHAT_ORDER_KEY = "dash_freshdesk_chat_order";
const PHONE_MAX_WIDTH = 667;
const PHONE_LANDSCAPE_MAX_HEIGHT = 500;

// A phone-sized viewport gets the fullscreen chat: narrow (a phone in portrait)
// or very short (a phone in landscape). Freshdesk's open window is a fixed 410px
// wide on every device, so on phones it runs off the edge unless fullscreen is set.
// The height limit sits well below a laptop's browser height, so laptops float.
const isPhoneSized = () =>
  window.innerWidth <= PHONE_MAX_WIDTH ||
  window.innerHeight <= PHONE_LANDSCAPE_MAX_HEIGHT;

type FreshdeskWidget = NonNullable<Window["fdWidget"]>;

// Keeps fullscreen in step with the viewport after the widget has started, so
// rotating a phone or resizing a window needs no reload. Freshdesk sizes an open
// chat only when it opens, so an open chat is closed and reopened when its mode
// changes; the conversation itself stays.
const keepFullscreenInStep = (widget: FreshdeskWidget, startedFullscreen: boolean) => {
  let fullscreen = startedFullscreen;
  window.addEventListener("resize", () => {
    const next = isPhoneSized();
    if (next === fullscreen) return;
    fullscreen = next;
    widget.setConfig?.({ fullscreen });
    if (widget.isOpen?.()) {
      widget.close?.();
      widget.open();
    }
  });
};

const getConfig = () => ({
  host: process.env.REACT_APP_FRESHDESK_CHAT_HOST || "",
  token: process.env.REACT_APP_FRESHDESK_CHAT_TOKEN || "",
  widgetId: process.env.REACT_APP_FRESHDESK_CHAT_WIDGET_ID || "",
});

export const isFreshdeskChatEnabled = () => {
  const { host, token, widgetId } = getConfig();
  return Boolean(host && token && widgetId);
};

export interface IChatTripContext {
  orderId: string;
  receiptUrl: string;
  pageLanguage: "en" | "zh";
  hotelName?: string | undefined;
}

// Only trip identifiers go to Freshdesk — never the guest's name or any phone
// number from the order (R2 AC 2). The order payload can carry the booker's phone.
export const buildTicketProperties = (ctx: IChatTripContext) => {
  const parts = ["Web receipt", `Order ${ctx.orderId}`];
  if (ctx.hotelName) parts.push(ctx.hotelName);
  parts.push(`Page ${ctx.pageLanguage.toUpperCase()}`);
  return {
    subject: parts.join(" · "),
    cf_order_id: ctx.orderId,
    cf_receipt_link: ctx.receiptUrl,
    cf_page_language: ctx.pageLanguage,
    cf_hotel_name: ctx.hotelName || "",
  };
};

// Freshdesk remembers the visitor per browser, not per receipt, so a second receipt
// opened in the same browser would join the first receipt's conversation. We note
// which order the remembered visitor belongs to and start a new visitor when the
// order changes. Reopening the same receipt keeps its conversation (EC 2).
const readChatOrder = () => {
  try {
    return window.localStorage.getItem(CHAT_ORDER_KEY);
  } catch {
    return null;
  }
};

const rememberChatOrder = (orderId: string) => {
  try {
    window.localStorage.setItem(CHAT_ORDER_KEY, orderId);
  } catch {
    // Storage blocked (private mode): every load is a new visitor anyway.
  }
};

const startNewVisitorIfOrderChanged = async (widget: FreshdeskWidget, orderId: string) => {
  if (readChatOrder() !== orderId) {
    await widget.user?.clear?.();
  }
  rememberChatOrder(orderId);
};

let loading: Promise<void> | null = null;

const startWidget = (ticketProperties: Record<string, unknown>) => {
  const { host, token, widgetId } = getConfig();
  return new Promise<void>((resolve, reject) => {
    const fail = (error: unknown, script?: HTMLScriptElement) => {
      script?.remove();
      reject(error instanceof Error ? error : new Error(String(error)));
    };
    const init = (script?: HTMLScriptElement) => {
      const widget = window.fdWidget;
      if (!widget) return fail(new Error("Freshdesk chat did not start"), script);
      try {
        const fullscreen = isPhoneSized();
        widget.init({
          host,
          token,
          widgetId,
          config: {
            headerProperty: { hideChatButton: true },
            fullscreen,
          },
          contactProperties: { language: "en" },
          ticketProperties,
        });
        keepFullscreenInStep(widget, fullscreen);
        resolve();
      } catch (error) {
        fail(error, script);
      }
    };
    if (window.fdWidget) return init();
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `${host.replace(/\/$/, "")}/webchat/js/widget.js`;
    script.onload = () => init(script);
    script.onerror = () =>
      fail(new Error("Freshdesk chat failed to load"), script);
    document.head.appendChild(script);
  });
};

// One load is shared by every Chat tap. Any failure clears it, so the guest's next
// tap tries again. The reset runs in .catch, after `loading` is assigned, so it also
// holds when the widget is already on the page and fails synchronously.
const loadWidget = (ticketProperties: Record<string, unknown>) => {
  if (!loading) {
    loading = startWidget(ticketProperties).catch((error) => {
      loading = null;
      throw error;
    });
  }
  return loading;
};

export const openFreshdeskChat = async (ctx: IChatTripContext) => {
  const ticketProperties = buildTicketProperties(ctx);
  await loadWidget(ticketProperties);
  if (window.fdWidget) await startNewVisitorIfOrderChanged(window.fdWidget, ctx.orderId);
  window.fdWidget?.ticket?.setProperties(ticketProperties);
  window.fdWidget?.open();
};
